import AxiosService from '@/plugins/axios';
import endpoints from '../../config/config';
import moment from '@/lib/time';
import Excel from 'exceljs';
import {
  fontNormal,
  sheetMargin,
  hightlightTableHead,
  blobToFile,
  imeiToPlateNumber,
  formatDate,
  formatDuration,
  hightlightRow,
  getOdoData,
  geofenceStartEndFromLocation,
  imeiToVehicleName,
  getMCRDriverName,
  generateLogoHeader,
  addLogoCompany,
} from './config';
import * as Sentry from '@sentry/vue';

function generateSummaryInfo({ resultsByImei, vehicles }) {
  /* summary by imei */
  const summaryByImei = {};
  Object.keys(resultsByImei).forEach((imei) => {
    const resultsByDate = {};

    resultsByImei[imei].forEach((row) => {
      const keyOfDate = moment.unix(row.startTime).startOf('day').unix();
      if (!(keyOfDate in resultsByDate)) {
        resultsByDate[keyOfDate] = {
          count: 0,
          speeds: [],
        };
      }

      resultsByDate[keyOfDate].count++;
      resultsByDate[keyOfDate].speeds.push(row.maxSpeed);
    });

    summaryByImei[imei] = Object.keys(resultsByDate).map((date) => {
      const speeds = resultsByDate[date].speeds;
      return {
        date: moment.unix(date).format('D MMM YYYY'),
        count: resultsByDate[date].count,
        // speeds: speeds,
        maxSpeed: Math.max(...speeds),
      };
    });
  });

  /* summary all */
  const summaryAllTableData = [];

  const summaryInfoList = Object.keys(summaryByImei).map((imei) => {
    const plateNumber = imeiToPlateNumber(vehicles, parseInt(imei));
    const count = summaryByImei[imei].reduce(
      (acc, current) => acc + current.count,
      0
    );
    const speeds = summaryByImei[imei].map((summary) => summary.maxSpeed);

    return {
      plateNumber,
      count,
      maxSpeed: Math.max(...speeds),
    };
  });
  summaryInfoList
    .sort((a, b) => b.maxSpeed - a.maxSpeed)
    .forEach((summaryInfo, idx) => {
      summaryAllTableData.push([
        idx + 1,
        summaryInfo.plateNumber,
        summaryInfo.count,
        summaryInfo.maxSpeed,
      ]);
    });

  return {
    columnSpec: [
      {
        key: 'no',
        width: 5,
        style: {
          numFmt: '0',
          font: fontNormal,
        },
      },
      {
        key: 'name',
        width: 20,
      },
      {
        key: 'count',
        width: 15,
      },
      {
        key: 'maxSpeed',
        width: 20,
      },
    ],
    summaryByImei: Object.keys(summaryByImei).map((imei) => {
      return {
        title: `สรุปรายงานความเร็วเกิน ของ รถทะเบียน ${imeiToPlateNumber(
          vehicles,
          parseInt(imei)
        )}`,
        tableHead: [
          'ที่',
          'วันที่',
          'จำนวนที่เกิน',
          'ความเร็วสูงสุด (กม./ชม.)',
        ],
        tableData: summaryByImei[imei].map((data, idx) => {
          return [idx + 1, data.date, data.count, data.maxSpeed];
        }),
      };
    }),
    summaryAll: {
      title: 'สรุปรายงานความเร็วเกินของทะเบียนรถทั้งหมด',
      tableHead: ['ที่', 'ทะเบียน', 'จำนวนที่เกิน', 'ความเร็วสูงสุด (กม./ชม.)'],
      tableData: summaryAllTableData,
    },
  };
}

function generateSheetInfo({
  resultsByImei,
  vehicles,
  geofences,
  dictOfDriver,
}) {
  const sheetInfoList = [];

  Object.keys(resultsByImei).forEach((imei) => {
    const results = resultsByImei[imei];

    const plateNumber = imeiToPlateNumber(vehicles, parseInt(imei));
    const vehicleName = imeiToVehicleName(vehicles, parseInt(imei));

    let totalDistance = 0;
    let totalDuration = 0;

    const tableRows = [];
    results.forEach((row) => {
      const resLoc = geofenceStartEndFromLocation(geofences, row);
      const distance = parseFloat(
        (row.positionDataEnd.io[65] - row.positionDataStart.io[65]) / 1000
      );

      tableRows.push([
        plateNumber,
        vehicleName,
        formatDate(row.startTime),
        formatDate(row.stopTime),
        formatDuration(row.startTime, row.stopTime),
        parseFloat(row.maxSpeed),
        parseFloat(
          getOdoData(vehicles, row.imei, row.positionDataEnd).toFixed(2)
        ),
        parseFloat(distance.toFixed()),
        resLoc.start,
        resLoc.end,
        `${row.positionDataStart.latitude / 10000000}, ${
          row.positionDataStart.longitude / 10000000
        }`,
        typeof dictOfDriver != 'undefined' && row.mcr_license_no in dictOfDriver
          ? dictOfDriver[row.mcr_license_no].name
          : getMCRDriverName(row.mcr_license_no, row.mcr_driver_name),
        row.startTime,
        row.stopTime,
      ]);

      /* calculate total */
      totalDistance = totalDistance + distance;
      totalDuration = totalDuration + parseInt(row.stopTime - row.startTime);
    });

    sheetInfoList.push({
      sheetName: plateNumber,
      title: `รายงานความเร็วเกิน ของ รถทะเบียน ${plateNumber} เวลาเริ่ม ${formatDate(
        results[0].startTime
      )} ถึง ${formatDate(results[results.length - 1].stopTime)}`,
      meta: `รวมระยะทางทั้งสิ้น ${totalDistance.toFixed(
        1
      )} กม. รวมเวลา ${moment('1970-01-01')
        .startOf('day')
        .seconds(totalDuration)
        .format('HH:mm:ss')} ชม.:นาที:วินาที`,
      columnSpec: [
        {
          key: 'plate_number',
          width: 15,
          style: {
            font: fontNormal,
          },
        },
        {
          key: 'name',
          width: 22,
          style: {
            numFmt: '0',
          },
        },
        {
          key: 'start',
          width: 20,
        },
        {
          key: 'stop',
          width: 20,
        },
        {
          key: 'totalTime',
          width: 12,
        },
        {
          key: 'maxSpeed',
          width: 20,
        },
        {
          key: 'cumOdometer',
          width: 12,
        },
        {
          key: 'totalDistance',
          width: 16,
        },
        {
          key: 'reversegeolocationstart',
          width: 40,
        },
        {
          key: 'reversegeolocationend',
          width: 40,
        },
        {
          key: 'geolocation',
          width: 30,
        },
        {
          key: 'driver',
          width: 15,
        },
        {
          key: 'timeStampStart',
          width: 15,
        },
        {
          key: 'timeStampStop',
          width: 15,
        },
      ],
      tableHead: [
        'ทะเบียนรถ',
        'ชื่อ',
        'วัน เวลา เริ่มต้น',
        'วัน เวลา สิ้นสุด',
        'รวมเวลา',
        'ความเร็วสูงสุด (กม./ชม.)',
        'เลขกม.สะสม',
        'รวมระยะทาง (กม.)',
        'ตำแหน่งเริ่มต้น',
        'ตำแหน่งสิ้นสุด',
        'ละติจูด ลองจิจูด',
        'ผู้ใช้รถ',
        'timestamp เริ่มต้น',
        'timestamp สิ้นสุด',
      ],
      tableData: tableRows,
    });
  });

  return sheetInfoList;
}

export const getOverSpeedReport = async (
  vehicles,
  imeis,
  start,
  stop,
  duration,
  geofences,
  dictOfDriver,
  logo
) => {
  try {
    const rawData = await AxiosService.post(
      `${endpoints.coreApi}/report-event`,
      {
        imeis: imeis,
        start: start,
        stop: stop,
        eventType: 'OVER_SPEED_END',
      }
    )
      .then((eventData) =>
        eventData.data.filter((row) => row.stopTime - row.startTime >= duration)
      )
      .catch((err) => {
        throw err;
      });

    const workbook = new Excel.Workbook();
    const pageProperties = {
      properties: {
        tabColor: {
          argb: 'FF41C173',
        },
      },
      pageSetup: {
        paperSize: 9,
        orientation: 'landscape',
      },
    };

    if (rawData.length > 0) {
      const resultsByImei = {};

      rawData.forEach((data) => {
        if (!(data.imei in resultsByImei)) {
          resultsByImei[data.imei] = [];
        }
        resultsByImei[data.imei].push(data);
      });

      /* summary sheet */
      const summarySheetInfo = generateSummaryInfo({ resultsByImei, vehicles });
      const summarySheet = workbook.addWorksheet('สรุป', pageProperties);
      summarySheet.pageSetup.margins = sheetMargin;
      summarySheet.columns = summarySheetInfo.columnSpec;

      let summaryTitleRow = 1;

      summarySheetInfo.summaryByImei.forEach((data) => {
        summarySheet.addRow([data.title]);
        summarySheet.addRow(data.tableHead);
        summarySheet.addRows(data.tableData);
        summarySheet.addRows([[], []]);

        hightlightRow(summarySheet, summaryTitleRow);
        hightlightTableHead(summarySheet, summaryTitleRow + 1); // 1 row under title row

        /* next starting data table row is number of current header (title, table head, ...etc) rows + number of data rows + number of blank rows */
        summaryTitleRow = summaryTitleRow + 2 + data.tableData.length + 2;
      });

      summarySheet.addRow([summarySheetInfo.summaryAll.title]);
      summarySheet.addRow(summarySheetInfo.summaryAll.tableHead);
      summarySheet.addRows(summarySheetInfo.summaryAll.tableData);

      hightlightRow(summarySheet, summaryTitleRow);
      hightlightTableHead(summarySheet, summaryTitleRow + 1); // 1 row under title row

      /* normal sheet */
      let imageLogoId;

      if (logo && logo.url) {
        const ext = logo.ext.substring(1);
        const myBase64Image = await generateLogoHeader(logo);
        imageLogoId = workbook.addImage({
          base64: myBase64Image,
          extension: ext,
        });
      }

      const normalSheetInfoList = generateSheetInfo({
        resultsByImei,
        vehicles,
        geofences,
        dictOfDriver,
      });

      normalSheetInfoList.forEach((sheetInfo) => {
        let startingRow = 1;

        const sheet = workbook.addWorksheet(
          sheetInfo.sheetName,
          pageProperties
        );
        sheet.pageSetup.margins = sheetMargin;
        sheet.columns = sheetInfo.columnSpec;

        if (logo && logo.url) {
          startingRow = 2;
          addLogoCompany(sheet, imageLogoId);
        }

        sheet.addRow([sheetInfo.title]);
        sheet.addRow([sheetInfo.meta]);
        sheet.addRow([]);
        sheet.addRow(sheetInfo.tableHead);
        sheet.addRows(sheetInfo.tableData);

        hightlightTableHead(sheet, startingRow + 3);
        hightlightRow(sheet, startingRow);
        hightlightRow(sheet, startingRow + 1);
      });
    } else {
      workbook.addWorksheet('ไม่พบข้อมูล', pageProperties);
    }

    const fileName =
      'OverSpeed_Report_' +
      moment.unix(start).format('D_MMM_YY') +
      '-ถึง-' +
      moment.unix(stop).format('D_MMM_YY');

    return blobToFile(workbook, fileName);
  } catch (err) {
    console.error('getOverSpeedReport', err);
    Sentry.captureException(err);
    return;
  }
};

export default getOverSpeedReport;
